import React, { useEffect, useRef, useState } from "react";
import { Grid } from "@mui/material";
//#region types
import { TextInputProps } from "models/propTypes/wintFormPropsTypes";
import { WintSuperkollAPIModel as ApiModels } from "wint-api-client/dist/types";
import { DialogEnums, StyleEnums } from "models/enums";
//#endregion
//#region components
import TextInput from "components/common/wintForms/formFields/textInput";
import StyledButton from "components/common/buttons/styledButton";
import MessageBubble from "components/common/messages/messageBubble";
import Description from "components/common/wintForms/formLayout/description";
import DialogContainer from "components/common/dialogs/dialogContainer";
import RadioSelectionGroup from "components/common/forms/baseFields/radioSelectionGroup";
import useCustomerService from "serviceHooks/useCustomerService";
import { TranslationKeys } from "translations";
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
//#endregion

export interface CustomerGlnInputProps extends TextInputProps {
  organizationNumber?: string;
}

const recipientLabel = (recipient: ApiModels.CustomerGlnLookupResult) =>
  [
    recipient.name,
    recipient.department,
    recipient.street,
    recipient.postCode,
    recipient.town,
    `GLN ${recipient.glnNumber}`,
  ]
    .filter(Boolean)
    .join(" · ");

export default function GlnInput({ organizationNumber, ...props }: CustomerGlnInputProps) {
  const { lookupGln } = useCustomerService();
  const [loading, setLoading] = useState(false);
  const [recipients, setRecipients] = useState<ApiModels.CustomerGlnLookupResult[]>([]);
  const [selectedRecipient, setSelectedRecipient] = useState<ApiModels.CustomerGlnLookupResult>();
  const [selection, setSelection] = useState("");
  const [message, setMessage] = useState("");
  const [danger, setDanger] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const generation = useRef(0);
  const disabled = props.disabled || props.readonly;
  const text = LocalizationHelperInstance.getFormattedMessage;

  useEffect(() => {
    generation.current++;
    setLoading(false);
    setRecipients([]);
    setSelectedRecipient(undefined);
    setMessage("");
    setAnnouncement("");
    return () => {
      generation.current++;
    };
  }, [organizationNumber, disabled]);

  const select = (recipient: ApiModels.CustomerGlnLookupResult) => {
    props.setValue(recipient.glnNumber);
    props.setTouched(true, false);
    setSelectedRecipient(recipient);
    setRecipients([]);
    setMessage("");
    setAnnouncement(text(TranslationKeys.Customer.GlnFilled));
  };

  const search = async () => {
    if (disabled) return;
    setAnnouncement("");
    if (!organizationNumber?.trim()) {
      setDanger(true);
      setMessage(text(TranslationKeys.Customer.GlnOrganisationRequired));
      return;
    }
    const request = ++generation.current;
    setLoading(true);
    setMessage("");
    try {
      const results = await lookupGln(organizationNumber);
      if (request !== generation.current) return;
      if (results.length === 1) select(results[0]);
      else if (results.length > 1) {
        setSelection("");
        setRecipients(results);
      } else {
        setDanger(false);
        setMessage(text(TranslationKeys.Customer.GlnNoResults));
      }
    } catch (error) {
      if (request !== generation.current) return;
      setDanger(true);
      setMessage(
        text(
          (error as { codes?: string[] })?.codes?.includes("InvalidOrgNr")
            ? TranslationKeys.Customer.GlnOrganisationRequired
            : TranslationKeys.Customer.GlnLookupFailed
        )
      );
    } finally {
      if (request === generation.current) setLoading(false);
    }
  };

  return (
    <>
      <Grid container direction="column" className={cssExports.formFieldMessageAttachElemTop}>
        <span className="visuallyHidden" role="status">
          {message || announcement}
        </span>
        <MessageBubble
          message={message || undefined}
          type={danger ? StyleEnums.DisplayType.Danger : StyleEnums.DisplayType.Info}
          placement="top"
          portalToBody
        />
        <Grid container item>
          <Grid item xs>
            <TextInput
              {...props}
              hasError={props.hasError || Boolean(message && danger)}
              onChange={(event) => {
                generation.current++;
                setLoading(false);
                setRecipients([]);
                setSelectedRecipient(undefined);
                setMessage("");
                setAnnouncement("");
                props.onChange(event);
              }}
            />
          </Grid>
          <Grid item className={cssExports.inputButton}>
            <StyledButton type="button" onClick={search} isSubmitting={loading} disabled={disabled} testId="glnSearch">
              {text(TranslationKeys.Common.Search)}
            </StyledButton>
          </Grid>
        </Grid>
        {selectedRecipient && selectedRecipient.glnNumber === props.value && (
          <Description>{recipientLabel(selectedRecipient)}</Description>
        )}
      </Grid>
      <DialogContainer
        isOpen={recipients.length > 1}
        title={text(TranslationKeys.Customer.GlnSelectRecipient)}
        dialogType={DialogEnums.DialogType.Content}
        maxWidth="sm"
        fullWidth
        closeDialog={() => setRecipients([])}
        actions={[
          {
            text: text(TranslationKeys.Customer.GlnUseSelection),
            disabled: !selection || disabled,
            type: StyleEnums.DisplayType.Info,
            onClick: async () => {
              const recipient = recipients.find((item) => item.glnNumber === selection);
              if (recipient && !disabled) select(recipient);
            },
          },
        ]}
      >
        <div>
          <p>
            {text(TranslationKeys.Customer.GlnSelectionHelp, undefined, {
              count: recipients.length,
              organizationNumber,
            })}
          </p>
          <RadioSelectionGroup
            aria-label={text(TranslationKeys.Customer.GlnSelectRecipient)}
            value={selection}
            onChange={(event) => setSelection(event.target.value)}
            items={recipients.map((recipient) => ({ id: recipient.glnNumber!, value: recipientLabel(recipient) }))}
          />
        </div>
      </DialogContainer>
    </>
  );
}
